import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AutoEnrollmentContract } from '@/AutoEnrollment/application'
import { AutoEnrollmentModel, AutoEnrollmentRepository } from '@/AutoEnrollment/domain'
import {
  IUseAutoEnrollmentProducts,
  useAutoEnrollmentProducts,
} from '@/AutoEnrollment/ui/hooks'
import { ApiContractFormErrorFieldAdapter } from '@/Contract/infrastructure/ApiContractAdapter'
import {
  FormReturnType,
  Step1Form,
  Step2Form,
  useProductContractingFormManager,
} from '@/Contract/ui/hooks'
import { LogaltyResponseStatus } from '@/Logalty/domain'
import { IUseLogaltyFirmController, useLogaltyFirmController } from '@/Logalty/ui/hooks'
import { HttpModel, MessagesModel } from '@/Shared/domain'
import { useMetaResponseHandler } from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { navigationRoutes } from '@/Shared/ui/routers'

export interface FormattedProduct {
  productId: number
  productServices: number[]
}

export interface IUseProductsContractingController {
  getCurrentStepForm: (step?: number) => FormReturnType
  contractProductStep: number
  nextStep: () => void
  previousStep: () => void
  navigateToOrigin: () => void
  autoEnrollmentModel: AutoEnrollmentModel | null
  documents: IUseAutoEnrollmentProducts
  logalty: IUseLogaltyFirmController
  saveStep1: (data: Step1Form) => Promise<void>
  saveStep2: (data: Step2Form) => Promise<void>
  saveStep3: () => Promise<void>
}

export const useProductsContractingController = (
  autoEnrollmentRepository: AutoEnrollmentRepository,
  origin = navigationRoutes.home,
): IUseProductsContractingController => {
  const [autoEnrollmentModel, setAutoEnrollmentModel] =
    useState<AutoEnrollmentModel | null>(null)

  const { getCurrentForm, initForms } = useProductContractingFormManager()
  const documents = useAutoEnrollmentProducts(autoEnrollmentRepository, true)

  const getSignStatusFunction = (): Promise<HttpModel<LogaltyResponseStatus>> => {
    return AutoEnrollmentContract(
      autoEnrollmentRepository,
    ).getAutoEnrollmentLogaltyStatus(autoEnrollmentModel?.processType || 2)
  }
  const logalty = useLogaltyFirmController(getSignStatusFunction)

  const { startLoading, stopLoading } = useLoader()
  const { handleMetaResponse } = useMetaResponseHandler()
  const navigate = useNavigate()

  const numberOfSteps = 5
  const [contractProductStep, setContractProductStep] = useState<number>(1)

  const [productsData, setProductsData] = useState<FormattedProduct[] | null>(null)

  const nextStep = (): void => {
    setContractProductStep(Math.min(contractProductStep + 1, numberOfSteps))
    window.scrollTo(0, 0)
  }

  const previousStep = (): void => {
    setContractProductStep(Math.max(contractProductStep - 1, 1))
    window.scrollTo(0, 0)
  }

  const navigateToOrigin = (): void => {
    navigate(origin)
  }

  const getAutoEnrollmentData = async (): Promise<void> => {
    startLoading()

    const response = await AutoEnrollmentContract(
      autoEnrollmentRepository,
    ).getAutoEnrollmentProductData()

    stopLoading()

    if (handleMetaResponse(response?.meta, undefined, { notifySuccess: false })) {
      if (response.data) {
        setAutoEnrollmentModel(response.data)

        const conditions = await documents.getDefaultConditionsData()
        await documents.getDocumentsData(response.data.processType)
        await initForms(conditions, response.data)
      }
    }
  }

  const getCurrentStepForm = (step?: number): FormReturnType => {
    if (step) {
      return getCurrentForm(step)
    }
    return getCurrentForm(contractProductStep)
  }

  const saveStep1 = async (data: Step1Form): Promise<void> => {
    const mustProductsFormated =
      data?.products?.map(
        p =>
          ({
            productId: Number(p.productId),
            productServices:
              p?.productServices?.map(subProduct => subProduct.serviceId) || [],
          }) as FormattedProduct,
      ) || []
    setProductsData(mustProductsFormated)

    nextStep()
  }

  const saveStep2 = async (data: Step2Form): Promise<void> => {
    startLoading()

    if (
      autoEnrollmentModel?.billingOwnerData?.isEditable ||
      autoEnrollmentModel?.billingOwnerData?.isDocumentEditable
    ) {
      const response = await AutoEnrollmentContract(
        autoEnrollmentRepository,
      ).updateOwnerData(data)
      stopLoading()
      const responseSuccess = handleMetaResponse(response?.meta, undefined, {
        notifySuccess: false,
      })
      if (responseSuccess) {
        documents.getDocumentsData(autoEnrollmentModel?.processType || 2).then(() => {
          nextStep()
        })
      } else {
        // Error management
        const form = getCurrentStepForm(2) as FormReturnType
        if (response?.meta.messages.length > 0) {
          response?.meta?.messages.forEach((errorMessage: MessagesModel) => {
            const errorField = ApiContractFormErrorFieldAdapter(errorMessage)
            if (errorField) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              form.setError(errorField as any, {
                type: 'value',
                message: errorMessage.description,
              })
            }
          })
        }
      }
    }
  }

  const saveStep3 = async (): Promise<void> => {
    startLoading()
    const response = await AutoEnrollmentContract(
      autoEnrollmentRepository,
    ).saveAutoEnrollmentStep3({
      processType: autoEnrollmentModel?.processType || 2,
      products: productsData || [],
      isIAERestaurant: true,
    })

    stopLoading()

    if (
      handleMetaResponse(response?.meta, undefined, {
        notifySuccess: false,
      })
    ) {
      if (!response?.data?.signUrl || !response?.data?.signSAML) {
        logalty.setSignStatus(LogaltyResponseStatus.Error)
        setContractProductStep(5)
        window.scrollTo(0, 0)
      } else {
        logalty.setLogalty({
          signUrl: response.data.signUrl,
          signSAML: atob(response.data.signSAML),
        })
        nextStep()
      }
    }

    nextStep()
  }

  useEffect(() => {
    getAutoEnrollmentData()
  }, [])

  return {
    getCurrentStepForm,
    contractProductStep,
    nextStep,
    previousStep,
    navigateToOrigin,
    autoEnrollmentModel,
    documents,
    logalty,
    saveStep1,
    saveStep2,
    saveStep3,
  }
}
